<template>
  <Dialog
    class="offer-image-preview"
    v-model:show="state.show"
    teleport="body"
  >
    <Tabs
      class="offer-image-preview__tabs"
      v-model:active="activeTab"
      @change="onTabChange"
    >
      <Tab
        v-for="tab of tabs"
        :title="tab.title"
        :name="tab.name"
        class="offer-image-preview__tab"
      >
        <Image
          v-for="image in tab.images"
          class="offer-image-preview__thumbnail"
          :class="{
            'offer-image-preview__thumbnail--active': image.index === swiperIndex
          }"
          :src="image.image"
          :key="image.index"
          @mouseenter="swiperIndex = image.index"
        />
      </Tab>
    </Tabs>
    <div class="offer-image-preview__wrapper">
      <p class="offer-image-preview__title">
        {{ title }}
      </p>
      <div class="offer-image-preview__content">
        <Swiper
          class="offer-image-preview__swiper"
          :modules="[Zoom, Virtual]"
          :initial-slide="swiperIndex"
          @swiper="onSwiperInited"
          zoom
          virtual
        >
          <SwiperSlide
            class="offer-image-preview__slide"
            v-for="(image, i) in images"
            :key="i"
            :virtual-index="i"
          >
            <div class="swiper-zoom-container">
              <Image
                class="offer-image-preview__image"
                :src="image"
                :alt="'Image ' + i"
              />
            </div>
          </SwiperSlide>
        </Swiper>
        <div
          class="offer-image-preview__navigation offer-image-preview__navigation--left"
          :class="{
            'offer-image-preview__navigation--disabled': swiperIndex <= 0
          }"
          @click="onPrev"
        >
          <Icon name="icon-arrow-left-l" />
        </div>
        <div
          class="offer-image-preview__navigation offer-image-preview__navigation--right"
          :class="{
            'offer-image-preview__navigation--disabled': swiperIndex >= images.length - 1
          }"
          @click="onNext"
        >
          <Icon name="arrow-right-l-2" />
        </div>
        <div class="offer-image-preview__tools">
          <Tooltip
            content="Translate"
            placement="left"
            class="offer-image-preview__tool"
            @click="onTranslate"
          >
            <Icon name="translate-l" />
          </Tooltip>
          <Tooltip
            content="Find Similar"
            placement="left"
            class="offer-image-preview__tool"
            @click="$emit('findSimilar', images[swiperIndex])"
          >
            <Icon name="icon-image-search-similar" />
          </Tooltip>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Zoom, Virtual } from 'swiper/modules'
import { Icon } from '@/components/Icon'
import { Tabs, Tab } from '@/components/Tab'
import { Dialog } from '@/components/Dialog'
import { Image } from '@/components/Image'
import { Tooltip } from '@/components/Tooltip'
import { useImagePreview } from './useImagePreview'
import { useGoogleImageTranslate } from '@/hooks/useGoogleImageTranslate'

const { state } = useImagePreview()
const { translate } = useGoogleImageTranslate()

const props = defineProps({
  title: {
    type: String,
    default: null
  },
  /**
   * @type {Array<string>}
   */
  mainImages: {
    type: Array,
    default: () => []
  },
  /**
   * @type {Array<{ attributeNameTrans, skuImageUrl, valueTrans }>}
   */
  variantImages: {
    type: Array,
    default: () => []
  },
  /**
   * @type {Array<string>}
   */
  descriptionImages: {
    type: Array,
    default: () => []
  }
})

defineEmits(['findSimilar'])

const tabs = computed(() => {
  const tabs = []
  if (props.mainImages.length > 0) {
    tabs.push({
      title: 'Main Images',
      name: 'main',
      images: props.mainImages.map((item, i) => ({ index: i, image: item }))
    })
  }
  if (props.variantImages.length > 0) {
    const baseIndex = props.mainImages.length
    tabs.push({
      title: 'Variant Images',
      name: 'variant',
      images: props.variantImages.map((item, i) => ({ index: baseIndex + i, image: item.skuImageUrl }))
    })
  }
  if (props.descriptionImages.length > 0) {
    const baseIndex = props.mainImages.length + props.variantImages.length
    tabs.push({
      title: 'Description Images',
      name: 'description',
      images: props.descriptionImages.map((item, i) => ({ index: baseIndex + i, image: item }))
    })
  }
  return tabs
})
const activeTab = ref(state.type)

const onTabChange = (tab) => {
  if (tab === 'main') {
    swiperIndex.value = 0
  } else if (tab === 'variant') {
    swiperIndex.value = props.mainImages.length
  } else if (tab === 'description') {
    swiperIndex.value = props.mainImages.length + props.variantImages.length
  }
}

const swiperRef = ref(null)

const onSwiperInited = (swiper) => {
  swiperRef.value = swiper
}

const swiperIndex = ref(0)

watch(() => [state.index, state.type], ([index, tab]) => {
  if (tab === 'main') {
    swiperIndex.value = index
  } else if (tab === 'variant') {
    swiperIndex.value = props.mainImages.length + index
  } else if (tab === 'description') {
    swiperIndex.value = props.mainImages.length + props.variantImages.length + index
  }
}, {
  immediate: true
})

watch(swiperIndex, (swiperIndex) => {
  if (swiperIndex < props.mainImages.length) {
    activeTab.value = 'main'
  } else if (swiperIndex < props.mainImages.length + props.variantImages.length) {
    activeTab.value = 'variant'
  } else {
    activeTab.value = 'description'
  }
  swiperRef.value?.slideTo(swiperIndex)
})

const images = computed(() => {
  return [].concat(props.mainImages, props.variantImages.map(item => item.skuImageUrl), props.descriptionImages)
})

const title = computed(() => {
  if (activeTab.value !== 'variant') return ''
  const variant = props.variantImages[swiperIndex.value - props.mainImages.length]
  return `${variant.attributeNameTrans}: ${variant.valueTrans}`
})

const onPrev = () => {
  if (swiperIndex.value > 0) {
    swiperIndex.value -= 1
  }
}

const onNext = () => {
  if (swiperIndex.value < images.value.length - 1) {
    swiperIndex.value += 1
  }
}

const onTranslate = () => {
  translate(images.value[swiperIndex.value], props.title)
}
</script>

<style lang="less">
.offer-image-preview {
  max-width: 1056px;
  width: 80%;
  background-color: #FFFFFF;
  user-select: none;

  .dialog__content {
    display: flex;
  }

  &__tabs {
    flex: 0;
    min-width: 43%;

    .tabs-content {
      height: 480px;
      overflow: scroll;
      padding: 32px 0 4px 4px;
    }

    :deep(.tabs-nav__line) {
      background-color: #052784;
    }
  }

  &__tab {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 12px;
  }

  &__thumbnail {
    aspect-ratio: 1;
    width: 80px;
    border-radius: 4px;
    border: 1px solid #EBF1F5;
    cursor: pointer;

    &--active {
      outline-offset: 2px;
      outline: 2px solid #052784;
    }
  }

  &__wrapper {
    flex: 1;
    width: 0;
  }

  &__title {
    margin-top: 30px;
    padding: 0 64px;
    text-align: center;
    font-family: mon-600;
    font-size: 14px;
    line-height: 18px;
    height: 18px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__content {
    position: relative;
    margin-top: 16px;
    padding: 0 64px;
  }

  &__navigation, &__tool {
    height: 40px;
    width: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 24px;
    color: #232323;
    cursor: pointer;
    box-shadow: 0px 4px 8px 0px #14141414, 0px -2px 4px 0px #1414140A;
    border-radius: 50%;
  }

  &__navigation {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);

    &--disabled {
      color: #B2B7C2;
      cursor: not-allowed;
    }

    &--left {
      left: 0;
    }

    &--right {
      right: 0;
    }
  }

  &__swiper {
    border-radius: 16px;
    width: 100%;
    aspect-ratio: 1;
    cursor: grab;
  }

  &__image {
    width: 100%;
    height: 100%;
  }

  &__tools {
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
}
</style>
